/**
 * Sauvegarde locale versionnée (§4, étape 8) — **logique pure**.
 *
 * Ce module ne touche pas à `localStorage` : il transforme un snapshot en texte et
 * inversement, en validant ce qu'il relit. Le stockage est branché par `net/storage`.
 * C'est ce découpage qui rend la migration testable sans navigateur.
 *
 * **Une sauvegarde relue n'est jamais digne de confiance.** Elle vient du disque d'un
 * appareil qu'on ne contrôle pas : elle peut être tronquée par un onglet tué en pleine
 * écriture, éditée à la main, ou produite par une version plus ancienne — voire plus
 * récente, si le joueur a ouvert le jeu sur un autre appareil avant de revenir. Chaque
 * champ est donc revalidé, et un défaut fait repartir sur une ville neuve plutôt que de
 * propager un état incohérent dans tout le moteur.
 *
 * **Le format est déjà multi-îles** (§4) : rien à migrer le jour de l'archipel.
 */

import { BOOSTS } from '@/config/balance';
import { decorationBySprite } from '@/config/decorations';
import { createSnapshot, SNAPSHOT_VERSION } from './gameState';
import type { Boost } from './gameState';
import { dayNumber } from './quests';
import type {
  BackpackItem,
  CitySnapshot,
  IslandState,
  Monument,
  PlacedBuilding,
  VisitorTrace,
} from './gameState';

/** Ce qu'on a fait d'une sauvegarde relue — utile pour l'écran de retour du §7. */
export type LoadOutcome =
  | { readonly kind: 'loaded'; readonly snapshot: CitySnapshot }
  | { readonly kind: 'empty'; readonly snapshot: CitySnapshot }
  | { readonly kind: 'corrupt'; readonly snapshot: CitySnapshot; readonly reason: string }
  | { readonly kind: 'future'; readonly snapshot: CitySnapshot; readonly version: number };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Nombre fini, sinon le repli. Attrape `NaN`, `Infinity` et les chaînes numériques. */
function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

/**
 * Les accélérations d'une sauvegarde, quel que soit son âge.
 *
 * Deux formats coexistent, et il n'y a aucun numéro de version à interroger : les
 * sauvegardes d'avant G§3 portent `rushMultiplier` / `rushUntil`, celles d'après
 * portent `boosts`. On lit donc ce qui est là — la présence d'une liste valide fait
 * foi, sinon on convertit l'ancien couple.
 *
 * L'ancienne accélération devient une entrée de source `gems` : c'est ce qu'elle
 * était (seule la boutique pouvait l'accorder à l'époque), et se tromper de source
 * priverait le joueur de son emplacement de pub gratuite pendant la durée restante.
 *
 * Rien n'est cru sur parole : le multiplicateur est borné au plafond de catalogue et
 * la liste au nombre maximal d'entrées. Une sauvegarde s'édite à la main.
 */
function readBoosts(parsed: Record<string, unknown>): Boost[] {
  const clamp = (multiplier: number): number =>
    Math.min(BOOSTS.maxProduct, Math.max(1, multiplier));

  if (Array.isArray(parsed.boosts)) {
    const out: Boost[] = [];
    for (const entry of parsed.boosts) {
      if (!isRecord(entry)) continue;
      const source = str(entry.source, '');
      if (source !== 'gems' && source !== 'ad' && source !== 'event') continue;
      const multiplier = clamp(num(entry.multiplier, 1));
      const until = Math.max(0, num(entry.until, 0));
      if (multiplier <= 1 || until <= 0) continue;
      out.push({ source, multiplier, until });
    }
    return out.slice(-BOOSTS.maxEntries);
  }

  // Format d'avant G§3. Une accélération expirée ne se ressuscite pas : `until` la
  // périme d'elle-même, et l'entrée disparaîtra à la première écriture.
  const multiplier = clamp(num(parsed.rushMultiplier, 1));
  const until = Math.max(0, num(parsed.rushUntil, 0));
  return multiplier > 1 && until > 0 ? [{ source: 'gems', multiplier, until }] : [];
}

function readBuilding(value: unknown): PlacedBuilding | null {
  if (!isRecord(value)) return null;

  const type = str(value.type, '');
  const id = str(value.id, '');
  if (type === '' || id === '') return null;

  const col = num(value.col, -1);
  const row = num(value.row, -1);
  if (col < 0 || row < 0) return null;

  return {
    id,
    type,
    skinId: str(value.skinId, `${type}-1`),
    col: Math.floor(col),
    row: Math.floor(row),
    // Le palier visuel du §7 n'est pas encore produit : on le borne quand même, pour
    // qu'une sauvegarde éditée ne fasse pas chercher un sprite de niveau 9.
    level: Math.min(3, Math.max(1, Math.floor(num(value.level, 1)))),
    // Sauvegardes d'avant l'orientation : on la recalcule depuis la position plutôt
    // que de tout aligner sur 0, ce qui figerait la ville dans un quadrillage.
    orientation: ((Math.floor(num(value.orientation, col * 7 + row * 3)) % 4) + 4) % 4,
    // Sauvegardes d'avant les variantes : on la recalcule depuis la position plutôt
    // que de tout ramener au modèle 0, ce qui alignerait la ville sur un seul modèle.
    variant: Math.max(0, Math.floor(num(value.variant, (col * 13 + row * 11) % 3))),
    placedAt: num(value.placedAt, 0),
  };
}

/**
 * Relit un monument (§16.4), symétrique de `readBuilding`.
 *
 * Les deux branches du ternaire de `readIsland` rendaient `fallback.monuments`, donc un
 * tableau vide : le premier rechargement effaçait tout monument. Le défaut était
 * invisible tant que rien n'en écrit — il fallait le corriger AVANT que la feature
 * n'existe, pas après avoir effacé l'héritage d'un joueur.
 */
function readMonument(value: unknown): Monument | null {
  if (!isRecord(value)) return null;

  const id = str(value.id, '');
  const type = str(value.type, '');
  if (id === '' || type === '') return null;

  const col = num(value.col, -1);
  const row = num(value.row, -1);
  if (col < 0 || row < 0) return null;

  return {
    id,
    type,
    col: Math.floor(col),
    row: Math.floor(row),
    // Une ère est un rang, jamais négatif ni fractionnaire.
    era: Math.max(0, Math.floor(num(value.era, 0))),
  };
}

/** Ce qu'une île relue peut rendre en plus d'elle-même : les objets payés à sauver. */
interface ReadIslandOutcome {
  readonly island: IslandState;
  /**
   * Décorations PAYÉES relues hors de la grille — rangées au sac à dos plutôt que
   * jetées (2.9c). Une trace de visiteur hors grille, elle, est simplement écartée :
   * elle repoussera. Un objet payé, rien ne le recréerait.
   */
  readonly stranded: readonly BackpackItem[];
}

function readIsland(value: unknown, fallback: IslandState): ReadIslandOutcome {
  if (!isRecord(value)) return { island: fallback, stranded: [] };

  const grid = isRecord(value.grid) ? value.grid : {};
  const cols = Math.max(1, Math.floor(num(grid.cols, fallback.grid.cols)));
  const rows = Math.max(1, Math.floor(num(grid.rows, fallback.grid.rows)));

  const rawBuildings = Array.isArray(value.buildings) ? value.buildings : [];
  const buildings = rawBuildings
    .map(readBuilding)
    .filter((b): b is PlacedBuilding => b !== null)
    // Un bâtiment hors de la grille serait invisible et bloquerait sa case à jamais.
    .filter((b) => b.col < cols && b.row < rows);

  const roads = (Array.isArray(value.roads) ? value.roads : [])
    .filter((key): key is string => typeof key === 'string')
    // Une route hors de la grille bloquerait une case invisible pour toujours.
    .filter((key) => {
      const parts = key.split(':');
      const col = Number(parts[0]);
      const row = Number(parts[1]);
      return (
        Number.isInteger(col) &&
        Number.isInteger(row) &&
        col >= 0 &&
        row >= 0 &&
        col < cols &&
        row < rows
      );
    });

  /*
    Traces des visiteurs — relues comme les monuments : champ par champ, bornées à la
    grille, la sorte validée par le lecteur du rendu (une sorte inconnue est ignorée à
    l'affichage, pas au chargement — le format peut grandir sans invalider les saves).
  */
  const traces: VisitorTrace[] = [];
  const stranded: BackpackItem[] = [];
  for (const entry of Array.isArray(value.traces) ? value.traces : []) {
    if (!isRecord(entry)) continue;
    const id = str(entry.id, '');
    const kind = str(entry.kind, '');
    const col = Math.floor(num(entry.col, -1));
    const row = Math.floor(num(entry.row, -1));
    if (id === '' || kind === '' || col < 0 || row < 0) continue;

    /*
      ⚠️ L'ORIGINE d'une trace fait partie de la trace. Ce lecteur ne relisait pas
      `bought` : au premier rechargement, une décoration PAYÉE redevenait une trace
      ordinaire — vendable au prix production et écartée au prestige suivant. Les
      sauvegardes écrites pendant la fenêtre du défaut sont déjà dépouillées ; leur
      origine se reconstruit depuis l'identifiant, `buyDecoration` nommant ses
      entités `dec-…` là où les traces de visiteurs naissent en `vt-…`. Un fait
      persisté, pas une supposition (règle 14).
    */
    const bought = entry.bought === true || id.startsWith('dec-');

    if (col >= cols || row >= rows) {
      // Hors de la grille. Une trace de visiteur s'écarte — elle repoussera. Une
      // décoration PAYÉE part au sac à dos (2.9c) : rien ne la recréerait.
      const definition = bought ? decorationBySprite(kind) : undefined;
      if (definition !== undefined) stranded.push({ id, decorationId: definition.id });
      continue;
    }

    const base = { id, kind, col, row, placedAt: num(entry.placedAt, 0) };
    traces.push(bought ? { ...base, bought: true } : base);
  }

  return {
    island: {
      id: str(value.id, fallback.id),
      theme: str(value.theme, fallback.theme),
      grid: { cols, rows },
      roads,
      buildings,
      monuments: Array.isArray(value.monuments)
        ? value.monuments.map(readMonument).filter((m): m is Monument => m !== null)
        : fallback.monuments,
      traces,
      /*
        Une sauvegarde d'avant la 0.99.0 n'a pas ce champ : elle repart sans case
        défrichée, donc avec tout son décor. C'est le bon repli — l'inverse (tout
        défricher par défaut) effacerait la nature d'une ville qu'on n'a pas touchée.
      */
      cleared: Array.isArray(value.cleared)
        ? value.cleared.filter((key): key is string => typeof key === 'string')
        : [],
    },
    stranded,
  };
}

/**
 * Relit le sac à dos (2.9c). Une entrée au catalogue inconnu est GARDÉE : le catalogue
 * peut grandir, et jeter un objet payé parce qu'une version ne le connaît pas encore
 * serait la perte même que le sac existe pour empêcher. La pose, elle, refusera tant
 * que l'entrée reste inconnue. Borné : une sauvegarde s'édite à la main.
 */
function readBackpack(parsed: Record<string, unknown>): BackpackItem[] {
  const out: BackpackItem[] = [];
  for (const entry of Array.isArray(parsed.backpack) ? parsed.backpack : []) {
    if (!isRecord(entry)) continue;
    const id = str(entry.id, '');
    const decorationId = str(entry.decorationId, '');
    if (id === '' || decorationId === '') continue;
    out.push({ id, decorationId });
    if (out.length >= 999) break;
  }
  return out;
}

/** Relit l'état des quêtes, en écartant tout ce qui n'est pas un nombre fini. */
function readQuests(value: unknown, fallback: CitySnapshot['quests']): CitySnapshot['quests'] {
  if (!isRecord(value)) return fallback;

  const baseline = isRecord(value.baseline) ? value.baseline : {};

  return {
    day: Math.floor(num(value.day, fallback.day)),
    baseline: {
      buildingsPlaced: Math.max(0, num(baseline.buildingsPlaced, 0)),
      coinsEarned: Math.max(0, num(baseline.coinsEarned, 0)),
      upgradesBought: Math.max(0, num(baseline.upgradesBought, 0)),
      landPurchases: Math.max(0, num(baseline.landPurchases, 0)),
      visitorsReceived: Math.max(0, num(baseline.visitorsReceived, 0)),
    },
    claimed: Array.isArray(value.claimed)
      ? value.claimed.filter((id): id is string => typeof id === 'string')
      : [],
    // Une chaîne négative ou démesurée viendrait d'une sauvegarde éditée à la main.
    streak: Math.min(9999, Math.max(1, Math.floor(num(value.streak, 1)))),
    streakClaimedDay: Math.floor(num(value.streakClaimedDay, -1)),
    /*
      Relances et rachat de chaîne (G§3, F1 et F2). Absents des sauvegardes d'avant :
      les replis à zéro donnent au joueur son quota complet, ce qui est exact — il
      n'a jamais pu en dépenser. `lostStreak` à 0 signifie « aucune chaîne à
      racheter » : on ne fabrique pas une dette rétroactive.
    */
    rerolls: Math.max(0, Math.floor(num(value.rerolls, 0))),
    rerollDay: Math.floor(num(value.rerollDay, -1)),
    lostStreak: Math.max(0, Math.floor(num(value.lostStreak, 0))),
    lostStreakDay: Math.floor(num(value.lostStreakDay, -1)),
    repairedWeek: Math.floor(num(value.repairedWeek, -1)),
  };
}

/** Relit l'état hebdomadaire. Sauvegardes antérieures : on repart de la semaine en cours. */
function readWeekly(value: unknown, fallback: CitySnapshot['weekly']): CitySnapshot['weekly'] {
  if (!isRecord(value)) return fallback;

  const baseline = isRecord(value.baseline) ? value.baseline : {};

  return {
    week: Math.floor(num(value.week, fallback.week)),
    baseline: {
      buildingsPlaced: Math.max(0, num(baseline.buildingsPlaced, 0)),
      coinsEarned: Math.max(0, num(baseline.coinsEarned, 0)),
      upgradesBought: Math.max(0, num(baseline.upgradesBought, 0)),
      landPurchases: Math.max(0, num(baseline.landPurchases, 0)),
      visitorsReceived: Math.max(0, num(baseline.visitorsReceived, 0)),
    },
    claimed: Array.isArray(value.claimed)
      ? value.claimed.filter((id): id is string => typeof id === 'string')
      : [],
  };
}

export function serialize(snapshot: CitySnapshot): string {
  return JSON.stringify(snapshot);
}

/**
 * Relit une sauvegarde.
 *
 * `now` sert à fabriquer la ville de repli et n'est jamais lu depuis la sauvegarde :
 * une horloge venue du disque ferait calculer des gains hors-ligne sur un temps forgé.
 */
export function deserialize(raw: string | null, now: number): LoadOutcome {
  const blank = createSnapshot(now);

  if (raw === null || raw.trim() === '') {
    return { kind: 'empty', snapshot: blank };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { kind: 'corrupt', snapshot: blank, reason: 'JSON illisible' };
  }

  if (!isRecord(parsed)) {
    return { kind: 'corrupt', snapshot: blank, reason: 'racine inattendue' };
  }

  const version = num(parsed.version, 0);

  if (version > SNAPSHOT_VERSION) {
    // Sauvegarde d'une version future : on ne l'écrase PAS et on ne la devine pas.
    // Le joueur a probablement joué sur un appareil plus à jour ; corrompre sa ville
    // serait pire que de lui montrer une partie vierge le temps qu'il se mette à jour.
    return { kind: 'future', snapshot: blank, version };
  }

  const rawIslands = Array.isArray(parsed.islands) ? parsed.islands : [];
  const fallbackIsland = blank.islands[0];
  if (fallbackIsland === undefined) {
    return { kind: 'corrupt', snapshot: blank, reason: 'repli invalide' };
  }

  const readIslands =
    rawIslands.length > 0 ? rawIslands.map((island) => readIsland(island, fallbackIsland)) : null;
  const islands = readIslands === null ? blank.islands : readIslands.map((r) => r.island);
  // Les décorations payées relues hors grille rejoignent le sac à dos — jamais jetées.
  const strandedDecorations = readIslands === null ? [] : readIslands.flatMap((r) => r.stranded);

  const claimed = Array.isArray(parsed.claimedMilestones)
    ? parsed.claimedMilestones.filter((id): id is string => typeof id === 'string')
    : [];

  const snapshot: CitySnapshot = {
    version: SNAPSHOT_VERSION,
    coins: Math.max(0, num(parsed.coins, blank.coins)),
    gems: Math.max(0, num(parsed.gems, 0)),
    /*
      Repère du registre serveur (ROADMAP 0.1). Une sauvegarde d'avant cette version
      n'a pas le champ : le repli 0 fait recréditer TOUT ce que le joueur a payé, ce
      qui est exactement le comportement voulu — il n'a encore rien absorbé du
      registre. Le crédit reste borné par ce que le serveur a réellement enregistré.
    */
    paidGemsClaimed: Math.max(0, Math.floor(num(parsed.paidGemsClaimed, 0))),
    totalProduced: Math.max(0, num(parsed.totalProduced, 0)),
    prestigeLevel: Math.max(0, Math.floor(num(parsed.prestigeLevel, 0))),
    // Jamais sous 1 : un multiplicateur nul figerait toute production sans le dire.
    prestigeMultiplier: Math.max(1, num(parsed.prestigeMultiplier, 1)),
    /*
      Repère de l'ère, borné à `totalProduced`.

      Une sauvegarde d'avant le prestige n'a pas ce champ : le repli 0 fait compter toute
      sa production pour l'ère en cours, ce qui est exact — elle n'a jamais prestigé. Le
      plafond, lui, protège d'une sauvegarde éditée : un repère supérieur au cumul
      rendrait la production de l'ère NÉGATIVE, donc le prestige à jamais indisponible.
    */
    eraStartedAt: Math.min(
      Math.max(0, num(parsed.totalProduced, 0)),
      Math.max(0, num(parsed.eraStartedAt, 0)),
    ),
    /*
      ── LES ACCÉLÉRATIONS, D'UN CHAMP UNIQUE À UNE LISTE (G§3, A3) ────────────

      Toutes les sauvegardes existantes portent `rushMultiplier` / `rushUntil` et
      **aucune** ne porte `boosts`. Sans cette conversion, `snapshot.boosts` vaut
      `undefined` au chargement et `activeBoosts()` échoue sur `undefined.filter` —
      c'est-à-dire un plantage au démarrage pour **tous les joueurs qui reviennent**,
      pas seulement ceux qui avaient une accélération en cours.

      C'est exactement la classe de défauts de la règle 21 : `grantBoost`,
      `activeBoosts` et `buyRush` sont chacun corrects, le format de sauvegarde est
      correct, et c'est ENTRE eux que la partie se perd. Aucun test unitaire de ces
      trois fonctions ne l'aurait vu.

      Une accélération relue ne peut ni dépasser sa valeur de catalogue ni durer
      indéfiniment : les deux se forgeraient à la main dans le stockage local.
    */
    boosts: readBoosts(parsed),
    /*
      Avances de production et laissez-passer de nuit (G§3 A1, A4). Absents des
      sauvegardes d'avant : les replis remettent simplement le quota du jour à zéro,
      ce qui est exact — un joueur qui n'a jamais pu en acheter n'en a pas consommé.
      `skipDay` prend le jour COURANT et non 0 : un jour zéro ferait croire à un
      quota d'une autre journée, donc épuisé sans raison.
    */
    skipDay: Math.max(0, Math.floor(num(parsed.skipDay, dayNumber(now)))),
    skipUsed: Math.max(0, Math.floor(num(parsed.skipUsed, 0))),
    nightPass: parsed.nightPass === true,
    landPurchases: Math.max(0, Math.floor(num(parsed.landPurchases, 0))),
    /*
      Sauvegardes d'avant la séparation des deux compteurs : le total vaut le compteur
      d'ère. C'est exact pour tout joueur qui n'a jamais prestigé — donc pour tous, la
      colonne étant née avec le prestige — et c'est de toute façon un PLANCHER : le
      total ne peut pas être inférieur au nombre d'extensions de l'ère en cours. D'où le
      `Math.max`, qui garde l'invariant vrai même sur une sauvegarde éditée à la main.
    */
    landPurchasesTotal: Math.max(
      Math.max(0, Math.floor(num(parsed.landPurchases, 0))),
      Math.max(0, Math.floor(num(parsed.landPurchasesTotal, 0))),
    ),
    visitorsReceived: Math.max(0, Math.floor(num(parsed.visitorsReceived, 0))),
    flowersSpawned: Math.max(0, Math.floor(num(parsed.flowersSpawned, 0))),
    landmarksSpawned: Array.isArray(parsed.landmarksSpawned)
      ? parsed.landmarksSpawned.filter((id): id is string => typeof id === 'string')
      : [],
    adsDay: Math.floor(num(parsed.adsDay, 0)),
    adsWatched: Math.max(0, Math.floor(num(parsed.adsWatched, 0))),
    buildingsPlaced: Math.max(0, Math.floor(num(parsed.buildingsPlaced, 0))),
    upgradesBought: Math.max(0, Math.floor(num(parsed.upgradesBought, 0))),
    // Sauvegardes d'avant les quêtes : on repart d'un état neuf plutôt que d'inventer
    // une chaîne de connexion que le joueur n'a pas gagnée.
    quests: readQuests(parsed.quests, blank.quests),
    weekly: readWeekly(parsed.weekly, blank.weekly),
    islands,
    backpack: [...readBackpack(parsed), ...strandedDecorations],
    claimedMilestones: claimed,
    createdAt: num(parsed.createdAt, now),
    updatedAt: num(parsed.updatedAt, now),
  };

  return { kind: 'loaded', snapshot };
}

/* ── Migration des emprises ────────────────────────────────────────────── */

/**
 * Redistribue les bâtiments dont les emprises se chevauchent.
 *
 * **Pourquoi cette migration existe.** Les emprises (`footprint`) sont arrivées après
 * que des villes ont été sauvegardées : jusque-là, un bâtiment n'occupait qu'une case,
 * quelle que soit sa taille à l'écran. Les sauvegardes existantes contiennent donc des
 * épiceries de 2 × 2 posées côte à côte sur des cases voisines — parfaitement valides
 * hier, superposées aujourd'hui. Le joueur voit sa ville s'écraser sur elle-même sans
 * avoir rien fait.
 *
 * **Aucun bâtiment n'est jamais supprimé.** C'est la seule règle non négociable ici :
 * mieux vaut une ville réarrangée qu'une ville amputée. Si la place manque, la grille
 * s'élargit — sans accorder d'extension payante, `landPurchases` restant intact.
 *
 * Les bâtiments sont traités du plus grand au plus petit : un 2 × 2 placé en premier
 * trouve un emplacement contigu, alors qu'en dernier il ne resterait que des trous
 * isolés d'une case.
 */
export function repackFootprints(
  island: IslandState,
  footprintOf: (type: string) => number,
): { readonly island: IslandState; readonly moved: number } {
  const occupied = new Set<string>();
  const key = (col: number, row: number): string => `${String(col)}:${String(row)}`;

  let { cols, rows } = island.grid;

  const fits = (type: string, col: number, row: number): boolean => {
    const size = footprintOf(type);
    if (col < 0 || row < 0 || col + size > cols || row + size > rows) return false;
    for (let dc = 0; dc < size; dc++) {
      for (let dr = 0; dr < size; dr++) {
        if (occupied.has(key(col + dc, row + dr))) return false;
      }
    }
    return true;
  };

  const claim = (type: string, col: number, row: number): void => {
    const size = footprintOf(type);
    for (let dc = 0; dc < size; dc++) {
      for (let dr = 0; dr < size; dr++) occupied.add(key(col + dc, row + dr));
    }
  };

  // Du plus encombrant au plus petit, puis par position d'origine pour que deux
  // exécutions sur la même sauvegarde rendent exactement le même résultat.
  const ordered = [...island.buildings].sort((a, b) => {
    const size = footprintOf(b.type) - footprintOf(a.type);
    if (size !== 0) return size;
    return a.col + a.row - (b.col + b.row) || a.id.localeCompare(b.id);
  });

  const placed: PlacedBuilding[] = [];
  let moved = 0;

  for (const building of ordered) {
    if (fits(building.type, building.col, building.row)) {
      claim(building.type, building.col, building.row);
      placed.push(building);
      continue;
    }

    // Recherche en spirale depuis la position d'origine : le bâtiment bouge le moins
    // possible, ce qui préserve l'allure générale de la ville.
    let target: { col: number; row: number } | null = null;

    for (let attempt = 0; attempt < 8 && target === null; attempt++) {
      const radius = Math.max(cols, rows);
      for (let r = 1; r <= radius && target === null; r++) {
        for (let dc = -r; dc <= r && target === null; dc++) {
          for (let dr = -r; dr <= r; dr++) {
            if (Math.max(Math.abs(dc), Math.abs(dr)) !== r) continue;
            if (fits(building.type, building.col + dc, building.row + dr)) {
              target = { col: building.col + dc, row: building.row + dr };
              break;
            }
          }
        }
      }

      if (target === null) {
        // Plutôt que de perdre un bâtiment, on agrandit le terrain. `landPurchases`
        // n'est pas touché : c'est une réparation, pas un cadeau.
        cols += 2;
        rows += 2;
      }
    }

    const spot = target ?? { col: 0, row: 0 };
    claim(building.type, spot.col, spot.row);
    placed.push({ ...building, col: spot.col, row: spot.row });
    moved++;
  }

  return {
    island: {
      ...island,
      grid: { cols, rows },
      // Ordre d'origine rétabli : le tri ne servait qu'au placement.
      buildings: placed.sort((a, b) => a.id.localeCompare(b.id)),
    },
    moved,
  };
}

/* ── Enveloppe scellée (A.2) ───────────────────────────────────────────── */

/*
  Pourquoi une somme de contrôle, alors que `deserialize` valide déjà champ par champ.

  Parce que la validation ne voit que ce que JSON.parse a survécu. Un onglet tué en
  pleine écriture peut laisser un préfixe de JSON qui se trouve être… du JSON valide —
  `{"version":3,"coins":12}` tronqué à `{"version":3}` parse parfaitement et efface
  la ville sans déclencher le moindre repli. La probabilité est faible ; multipliée par
  une écriture toutes les 5 secondes chez chaque joueur, elle finit par arriver à
  quelqu'un. Le CRC transforme « tronqué mais plausible » en « tronqué, donc rejeté »,
  et c'est le second emplacement qui fournit alors la bonne copie.
*/

/** Préfixe du format scellé. Le chiffre est celui du FORMAT d'enveloppe, pas du jeu. */
const ENVELOPE_PREFIX = 'VS1';

/** Table CRC-32 (polynôme réfléchi 0xEDB88320), construite une fois. */
const CRC_TABLE: readonly number[] = (() => {
  const table: number[] = [];
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = (c & 1) === 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table.push(c >>> 0);
  }
  return table;
})();

/**
 * CRC-32 d'une chaîne : un octet par unité UTF-16 qui tient dans un octet, deux sinon.
 *
 * Sur de l'ASCII — un JSON de sauvegarde l'est presque entièrement — le résultat est
 * donc le CRC-32 **canonique**, comparable à `crc32` de zlib ou de 7-Zip : précieux le
 * jour où l'on débogue une sauvegarde à la main. Les caractères au-delà participent
 * par leurs deux octets. Cette projection n'est pas injective (« BA » et U+4142
 * produisent les mêmes octets), et c'est sans importance : on ne cherche pas une
 * empreinte cryptographique, seulement qu'une troncature ou une altération locale
 * change la somme — ce que garantit la participation de chaque unité de la chaîne.
 */
export function crc32(text: string): number {
  let crc = 0xffffffff;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    crc = (CRC_TABLE[(crc ^ (code & 0xff)) & 0xff] ?? 0) ^ (crc >>> 8);
    if (code > 0xff) {
      crc = (CRC_TABLE[(crc ^ (code >>> 8)) & 0xff] ?? 0) ^ (crc >>> 8);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** Scelle un texte : `VS1|crc en hexadécimal sur 8|texte`. */
export function sealEnvelope(json: string): string {
  return `${ENVELOPE_PREFIX}|${crc32(json).toString(16).padStart(8, '0')}|${json}`;
}

export type EnvelopeOutcome =
  | { readonly ok: true; readonly json: string }
  | { readonly ok: false; readonly reason: 'empty' | 'format' | 'crc' };

/**
 * Ouvre une enveloppe scellée. Ne parse RIEN : la validation de contenu reste le
 * travail de `deserialize` — cette couche ne répond qu'à une question, « le texte
 * relu est-il celui qui a été écrit ? ».
 */
export function openEnvelope(raw: string | null): EnvelopeOutcome {
  if (raw === null || raw === '') return { ok: false, reason: 'empty' };

  // `VS1|xxxxxxxx|` = 13 caractères avant le contenu.
  if (!raw.startsWith(`${ENVELOPE_PREFIX}|`) || raw.length < 14 || raw[12] !== '|') {
    return { ok: false, reason: 'format' };
  }

  const declared = Number.parseInt(raw.slice(4, 12), 16);
  const json = raw.slice(13);

  if (!Number.isFinite(declared) || crc32(json) !== declared) {
    return { ok: false, reason: 'crc' };
  }

  return { ok: true, json };
}
